import { forwardRef } from 'react';
import { cn } from '../../lib/cn';

/* ── Variants ───────────────────────────────────────────────────────────────
   Add a new entry to `variants` or `sizes` to extend without touching
   existing callers. Pass `variant` and `size` as props.
──────────────────────────────────────────────────────────────────────────── */
// All variants get `hover:cursor-pointer` via the base className below;
// don't repeat it per-variant.
const variants = {
  primary:
    'bg-[hsl(var(--color-ink))] text-[hsl(var(--color-bone))] hover:opacity-90',
  secondary:
    'bg-[hsl(var(--color-cotton))] text-[hsl(var(--color-ink))] hover:bg-[hsl(var(--color-bone))]',
  outline:
    'border border-[hsl(var(--color-hairline))] bg-transparent text-[hsl(var(--color-ink))] hover:bg-[hsl(var(--color-cotton))]',
  ghost:
    'bg-transparent text-[hsl(var(--color-ink))] hover:bg-[hsl(var(--color-cotton))]',
  destructive: 'bg-[hsl(var(--color-oxblood))] text-[hsl(var(--color-bone))] hover:opacity-90',
} as const;

const sizes = {
  xs: 'h-6 px-2 text-xs',
  sm: 'h-8 px-3 text-sm',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-6 text-base',
  icon: 'h-9 w-9',
} as const;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius)] font-medium',
        'transition-colors focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-[hsl(var(--color-ring))] focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50 hover:cursor-pointer',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
